// One requirement, full width: evidence on the left, everything you decide from
// on the right.
//
// The outer row does NOT scroll — the document stays put and only the sidebar
// moves. Comparing a scan against a field you have to scroll to is the thing this
// layout exists to avoid.
import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography, Button, TextField, ProfileAvatar, Link } from '../../ui/core';
import { hairline, softFill, scrollY } from '../../ui/styles';
import { ChevronLeftIcon, ChevronRightIcon } from '../../ui/icons';
import {
  SectionLabel,
  Hair,
  ItemStatusChip,
  StatusDot,
  DocViewer,
  EmptyViewer,
  PendingBackend,
  timestamp,
} from './components';
import { DOC_LABELS } from './model';
import FieldsPanel from './FieldsPanel';
import DebugPanel from './DebugPanel';

/**
 * What a reviewer can decide.
 *
 * NEEDS_REVIEW is deliberately absent. It is the pipeline saying it could not settle
 * a document — by the time someone is looking at the scan they ARE the review, so
 * offering it as an outcome only lets them hand the document back to themselves. The
 * endpoint still accepts it for anything that needs to set it programmatically; this
 * is a UI choice, not a narrowing of the API.
 */
const DECISIONS = [
  { status: 'APPROVED', label: 'Approve', color: 'success' },
  { status: 'REJECTED', label: 'Reject', color: 'error' },
];


/**
 * Stands in for the driver-visible rejection note while that field is hidden.
 *
 * The server refuses a rejection without one, so something has to be sent. It is
 * generic on purpose: this lands in a column the driver app will eventually read,
 * so it must not carry operator commentary. Remove alongside the commented-out
 * field in DecisionForm.
 */
const REJECTION_PLACEHOLDER = 'Document rejected during review.';

/**
 * The decision form.
 *
 * Remounted per document row (`key` on the caller) so notes and the expiry never
 * carry over from the last document — a reviewer typing a rejection reason for
 * one insurance scan and having it appear on the next is how the wrong driver
 * gets told the wrong thing.
 */
const DecisionForm = ({ requirement, onDecide, busy }) => {
  const row = requirement.current;
  const [status, setStatus] = useState(null);
  const [reviewNote, setReviewNote] = useState(row?.reviewNote || '');
  const [internalNote, setInternalNote] = useState(row?.internalNote || '');

  // The row itself can change under us — deciding re-reads the record — so keep
  // the fields in step with what is actually stored.
  useEffect(() => {
    setStatus(null);
    setReviewNote(row?.reviewNote || '');
    setInternalNote(row?.internalNote || '');
  }, [row?.id, row?.status, row?.reviewNote, row?.internalNote]);

  if (!row) return null;

  const rejecting = status === 'REJECTED';
  // The internal note carries the reason while the driver-facing one is hidden —
  // see the commented-out field below. A rejection with no reason recorded
  // anywhere is the thing actually worth preventing.
  const blocked = busy || !status || (rejecting && !internalNote.trim());

  const submit = () => {
    onDecide({
      status,
      // The server requires a driver-visible note on a rejection, in the service
      // AND in the DAO. With the field hidden there is nothing to send, so this
      // stands in — deliberately generic, because the column IS driver-visible
      // and the internal note must not leak into it when that surface ships.
      // Restore the field below and this goes away.
      reviewNote: rejecting ? REJECTION_PLACEHOLDER : reviewNote.trim() || null,
      internalNote: internalNote.trim() || null,
      // expirationDate is deliberately never sent from here. Omitted means "keep
      // the stored value", so a decision cannot disturb a date corrected in the
      // fields panel below.
    });
  };

  return (
    <Box>
      <Stack direction="row" spacing={0.75} sx={{ mb: 1.5 }}>
        {DECISIONS.map((d) => (
          <Button
            key={d.status}
            size="small"
            variant={status === d.status ? 'contained' : 'outlined'}
            color={d.color}
            onClick={() => setStatus(status === d.status ? null : d.status)}
            sx={{ fontSize: '12.5px', textTransform: 'none', flex: 1 }}
          >
            {d.label}
          </Button>
        ))}
      </Stack>

      <Stack spacing={1.5}>
        {/* Hidden until the driver-facing note is actually surfaced in the app.
            Collecting a note that nobody ever reads is worse than not asking for
            it: the reviewer believes they have told the driver why, and the driver
            is never told. Restore this — and drop REJECTION_PLACEHOLDER above —
            when the app shows it.

        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label={rejecting ? 'Note to the driver (required)' : 'Note to the driver'}
          helperText="Shown to the driver in the app"
          value={reviewNote}
          onChange={(e) => setReviewNote(e.target.value)}
          error={rejecting && !reviewNote.trim()}
        />
        */}
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label={rejecting ? 'Internal note (required)' : 'Internal note'}
          helperText={
            rejecting
              ? 'Operator-only. Required on a rejection — it is the only place the reason is recorded.'
              : 'Operator-only — never shown to the driver'
          }
          value={internalNote}
          onChange={(e) => setInternalNote(e.target.value)}
          error={rejecting && !internalNote.trim()}
        />
        {/* Expiry is not here. It is a field of the document like any other, and it
            belongs next to the extracted date it should be checked against — two
            controls over one value is how they end up disagreeing. */}
        <Button variant="contained" disabled={blocked} onClick={submit} sx={{ textTransform: 'none' }}>
          {busy ? 'Saving…' : status ? `${DECISIONS.find((d) => d.status === status).label} document` : 'Pick a decision'}
        </Button>
      </Stack>
    </Box>
  );
};

/** Every row for this type, newest first, with the one that counts marked. */
const Versions = ({ rows, selectedId, onSelect }) => {
  if (!rows.length) return null;
  return (
    <Box>
      <SectionLabel sx={{ display: 'block', mb: 0.75 }}>
        {rows.length} version{rows.length === 1 ? '' : 's'}
      </SectionLabel>
      <Box sx={{ border: (t) => `1px solid ${hairline(t)}`, borderRadius: '8px', overflow: 'hidden' }}>
        {rows.map((row, i) => (
          <Stack
            key={row.id}
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={() => onSelect(row.id)}
            sx={{
              px: 1.25,
              py: 0.9,
              cursor: 'pointer',
              borderTop: i === 0 ? 'none' : (t) => `1px solid ${hairline(t)}`,
              backgroundColor: (t) => (row.id === selectedId ? softFill(t) : 'transparent'),
              '&:hover': { backgroundColor: (t) => softFill(t) },
            }}
          >
            <StatusDot value={row.status} size={15} />
            <Typography sx={{ fontSize: '12.5px', fontWeight: 600 }}>v{rows.length - i}</Typography>
            {/* When a version was uploaded is a moment, not a date. Two re-uploads
                in the same minute are exactly the case where a reviewer needs to
                know which came first. */}
            <Typography
              sx={{
                fontSize: '11.5px',
                color: 'text.secondary',
                flex: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {timestamp(row.createdAt)}
              {row.source ? ` · ${row.source}` : ''}
            </Typography>
            {row.isCurrent && (
              <Typography sx={{ fontSize: '11px', color: 'accent.main', fontWeight: 600 }}>current</Typography>
            )}
          </Stack>
        ))}
      </Box>
      {/* KTD10: the current row is the newest, but validity looks at the best
          row — so a pending v3 over an approved v2 still passes. Saying so here
          stops "why is this pending but not blocking" being a mystery. */}
      <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: 0.75 }}>
        Validity uses the best valid version, not the newest.
      </Typography>
    </Box>
  );
};

const Inspector = ({
  profile,
  requirement,
  driverName,
  photoUrl,
  userId,
  onBack,
  onStep,
  stepIndex,
  stepCount,
  onDecide,
  onUpload,
  viewingId,
  onViewVersion,
  extraction,
  extractionLoading,
  extractionError,
  fieldRows,
  onSaveFields,
  onReplay,
  busy,
}) => {
  // Which version is on screen lives in the URL, so a shared link opens on the
  // same one. Falls back to the current row: an absent or stale `v` — a version
  // from another requirement, or one since replaced — should show the document
  // that counts rather than nothing at all.
  const viewing = requirement.rows.find((r) => r.id === viewingId) || requirement.current;
  const owner = profile.owners.find((o) => o.key === requirement.group);

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ px: 2.5, py: 1.5, borderBottom: (t) => `1px solid ${hairline(t)}`, flexShrink: 0 }}
      >
        <ChevronLeftIcon
          onClick={onBack}
          sx={{ fontSize: 18, flexShrink: 0, cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
        />
        {/* Avatar and name are ONE link to the user record, the same unit and the
            same target the profile header uses — a reviewer deep in a document often
            wants the person, and the only route back was the chevron. Plain when the
            user id has not resolved, rather than linking to /users/undefined.

            The rest of the breadcrumb stays OUTSIDE it: sending "Registration" to a
            user record would be a surprising thing to click. */}
        <Stack
          component={userId ? Link : 'div'}
          {...(userId ? { to: `/users/${userId}` } : {})}
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            // flexShrink 0 and nowrap on BOTH halves of the breadcrumb. Flex children
            // shrink below their content width next to the flex:1 spacer below, which
            // is what wrapped this header before it was one element — splitting it in
            // two is only safe while neither half can shrink.
            flexShrink: 0,
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            color: 'inherit',
            ...(userId ? { cursor: 'pointer', '&:hover': { opacity: 0.75 } } : {}),
          }}
        >
          <ProfileAvatar name={driverName} photoUrl={photoUrl} colorKey={driverName} size={24} />
          <Typography sx={{ fontSize: '13.5px', fontWeight: 600 }}>{driverName}</Typography>
        </Stack>
        <Typography sx={{ fontSize: '13.5px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
          <Box component="span" sx={{ color: 'text.disabled', mr: 0.75, fontWeight: 400 }}>
            ·
          </Box>
          {owner && owner.kind === 'vehicle' && (
            <>
              {owner.title}
              <Box component="span" sx={{ color: 'text.disabled', mx: 0.75, fontWeight: 400 }}>
                ·
              </Box>
            </>
          )}
          {requirement.label}
        </Typography>
        <Box sx={{ flexShrink: 0 }}>
          <ItemStatusChip value={requirement.status} />
        </Box>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          {stepCount > 1 && (
            <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
              {stepIndex + 1} of {stepCount}
            </Typography>
          )}
          <ChevronLeftIcon
            onClick={() => onStep(-1)}
            sx={{ fontSize: 19, cursor: stepIndex > 0 ? 'pointer' : 'default', color: stepIndex > 0 ? 'text.secondary' : 'text.disabled' }}
          />
          <ChevronRightIcon
            onClick={() => onStep(1)}
            sx={{
              fontSize: 19,
              cursor: stepIndex < stepCount - 1 ? 'pointer' : 'default',
              color: stepIndex < stepCount - 1 ? 'text.secondary' : 'text.disabled',
            }}
          />
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2.5} sx={{ flex: 1, minHeight: 0, px: 2.5, py: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
          {viewing?.imageUrl ? (
            <DocViewer id={viewing.id} src={viewing.imageUrl} />
          ) : requirement.id === 'screening' ? (
            <EmptyViewer>
              A background check has no document to show. The report lives at Checkr.
            </EmptyViewer>
          ) : viewing ? (
            <EmptyViewer>
              This version’s stored URL could not be signed, so there is no image to show. The row is still decidable.
            </EmptyViewer>
          ) : (
            <EmptyViewer>Nothing on file for this requirement. Uploading creates the first version.</EmptyViewer>
          )}
        </Box>

        {/* The only thing that scrolls. 520 rather than 400: the fields table is two
            value columns inside it, and a contested field puts a date and its list of
            model names side by side in one of them — at 400 an ISO date wrapped
            mid-value, which for a value a reviewer is comparing character by
            character is the worst place to break a line. */}
        <Box sx={{ width: 520, flexShrink: 0, height: '100%', ...scrollY, pr: 0.5 }}>
          {/* No remedy preamble. The status chip in the header already says what
              state this is in, and the decision buttons say what can be done about
              it — a paragraph restating both pushed the actual controls down. */}
          <Stack spacing={2}>
            {requirement.id === 'screening' ? (
              <Box>
                <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mb: 1.5 }}>
                  A background check is not a document. Resolve it with a standing action from the pill in the header —
                  approving it here would not change the report.
                </Typography>
                <PendingBackend title="Screening history">
                  Past reports and the annual re-check trail are in screening_report, which has no admin read endpoint
                  yet. Only the latest result is available.
                </PendingBackend>
              </Box>
            ) : (
              <>
                {requirement.current ? (
                  <Box>
                    <SectionLabel sx={{ display: 'block', mb: 1 }}>Decision</SectionLabel>
                    <DecisionForm
                      key={requirement.current.id}
                      requirement={requirement}
                      onDecide={onDecide}
                      busy={busy}
                    />
                  </Box>
                ) : (
                  <Box>
                    <SectionLabel sx={{ display: 'block', mb: 1 }}>Upload on their behalf</SectionLabel>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mb: 1.25 }}>
                      Creates the first version of this {DOC_LABELS[requirement.documentType] || 'document'} and sends it
                      through the same verification the driver’s own uploads get.
                    </Typography>
                    <Button variant="outlined" onClick={onUpload} disabled={busy} sx={{ textTransform: 'none' }}>
                      Choose a file
                    </Button>
                  </Box>
                )}
                <Hair />
                <FieldsPanel
                  rows={fieldRows}
                  run={extraction?.latest}
                  loading={extractionLoading}
                  error={extractionError}
                  onSave={onSaveFields}
                  // Only with a row to re-run. With nothing on file the pipeline has
                  // no document to read, and uploading is the action instead.
                  onReplay={requirement.current ? onReplay : null}
                  busy={busy}
                />
                {/* Last: history is reference, not the work. It sat between the
                    decision and the fields, pushing the comparison below the fold. */}
                <Hair />
                {/* `viewing.id`, not viewingId — so the row highlighted is the one
                    actually on screen when `v` is absent or no longer exists. */}
                <Versions rows={requirement.rows} selectedId={viewing?.id} onSelect={onViewVersion} />
                {requirement.current && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={onUpload}
                    disabled={busy}
                    sx={{ textTransform: 'none', alignSelf: 'flex-start', fontSize: '12.5px' }}
                  >
                    Upload a replacement
                  </Button>
                )}
                {/* Last, and collapsed. It answers "why is this field blank/contested",
                    which is a question you only ask after the reviewer view failed to
                    answer it. */}
                <Hair />
                <DebugPanel run={extraction?.latest} />
              </>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default Inspector;
